import { MapingIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, GlobeIcon } from 'lucide-react';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { sitesApi } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  ResponsivePopover,
  ResponsivePopoverContent,
  ResponsivePopoverTrigger,
} from '@/components/ui/responsive-popover';

interface Site {
  id: string;
  name: string;
  code: string;
  imageUrl?: string | null;
}

interface SiteContextType {
  currentSite: Site | null;
  setCurrentSite: (site: Site | null) => void;
  sites: Array<Site & { role: string }>;
}

const SiteContext = createContext<SiteContextType>({
  currentSite: null,
  setCurrentSite: () => {},
  sites: [],
});

const SITE_STORAGE_KEY = 'opexmes-current-site';

const ALL_SITES_VALUE = '__all_sites__';

export function useSiteContext() {
  return useContext(SiteContext);
}

export function SiteProvider({ children }: { children: ReactNode }) {
  const [currentSite, setCurrentSite] = useState<SiteContextType['currentSite']>(() => {
    try {
      const stored = localStorage.getItem(SITE_STORAGE_KEY);
      if (stored === ALL_SITES_VALUE) return null;
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const { data: sitesData } = useQuery({
    queryKey: ['sites', 'my-sites'],
    queryFn: () => sitesApi.getMySites(),
    staleTime: 5 * 60 * 1000,
  });

  const sites = (sitesData as any) ?? [];

  useEffect(() => {
    if (!currentSite && sites.length > 0) {
      const stored = localStorage.getItem(SITE_STORAGE_KEY);
      if (stored !== ALL_SITES_VALUE) {
        setCurrentSite(sites[0]);
      }
    }
  }, [sites, currentSite]);

  const handleSetCurrentSite = (site: Site | null) => {
    setCurrentSite(site);
    if (site) {
      localStorage.setItem(SITE_STORAGE_KEY, JSON.stringify(site));
    } else {
      localStorage.setItem(SITE_STORAGE_KEY, ALL_SITES_VALUE);
    }
  };

  return (
    <SiteContext.Provider value={{ currentSite, setCurrentSite: handleSetCurrentSite, sites }}>
      {children}
    </SiteContext.Provider>
  );
}

function SiteIcon({ site, size = 16 }: { site: Site | null; size?: number }) {
  if (!site) {
    return (
      <GlobeIcon className="shrink-0 text-muted-foreground" style={{ width: size, height: size }} />
    );
  }
  if (site.imageUrl) {
    return (
      <img
        src={site.imageUrl}
        alt={site.name}
        className="shrink-0 rounded object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return <HugeiconsIcon icon={MapingIcon} size={size} className="shrink-0 text-muted-foreground" />;
}

function SitePopover({
  sites,
  currentSite,
  onSelect,
}: {
  sites: Array<Site & { role: string }>;
  currentSite: Site | null;
  onSelect: (site: Site | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const isAllSites = currentSite === null;

  return (
    <ResponsivePopover open={open} onOpenChange={setOpen}>
      <ResponsivePopoverTrigger asChild>
        <button className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors">
          <SiteIcon site={currentSite} />
          <span className="truncate">{isAllSites ? 'All Sites' : currentSite.code}</span>
        </button>
      </ResponsivePopoverTrigger>
      <ResponsivePopoverContent>
        <button
          className={cn(
            'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
            isAllSites && 'bg-accent',
          )}
          onClick={() => {
            onSelect(null);
            setOpen(false);
          }}
        >
          <GlobeIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">All Sites</span>
          {isAllSites && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
        </button>
        {sites.map((site) => (
          <button
            key={site.id}
            className={cn(
              'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
              currentSite?.id === site.id && 'bg-accent',
            )}
            onClick={() => {
              onSelect(site);
              setOpen(false);
            }}
          >
            <SiteIcon site={site} />
            <span className="truncate">
              {site.name} ({site.code})
            </span>
            {currentSite?.id === site.id && <Check className="ml-auto h-3.5 w-3.5 text-primary" />}
          </button>
        ))}
      </ResponsivePopoverContent>
    </ResponsivePopover>
  );
}

export function SiteSelector({ compact = false }: { compact?: boolean }) {
  const { currentSite, setCurrentSite, sites } = useSiteContext();
  const queryClient = useQueryClient();

  if (sites.length <= 1) return null;

  const handleSiteChange = (site: Site | null) => {
    setCurrentSite(site);
    queryClient.invalidateQueries();
  };

  return <SitePopover sites={sites} currentSite={currentSite} onSelect={handleSiteChange} />;
}
